import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import * as Icons from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { Reveal } from "./Reveal";
import { STATS, BIST_DATA } from "@/data/content";

function CountUp({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    const dur = 1400;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) requestAnimationFrame(step);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

export default function Statistics() {
  return (
    <section id="istatistikler" className="bg-white py-14 lg:py-16">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-6 items-stretch">
        {/* Stats card */}
        <Reveal className="rounded-2xl bg-navy p-8 lg:p-10">
          <h3 className="font-extrabold text-white text-2xl tracking-tight mb-8">RAKAMLARLA BORSA</h3>
          <div className="grid grid-cols-5 gap-4">
            {STATS.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.BarChart3;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="text-center"
                >
                  <Icon className="w-6 h-6 text-white mx-auto mb-2.5" strokeWidth={1.7} />
                  <div className="text-white/70 text-[11px] leading-tight mb-3 min-h-[28px]">{s.label}</div>
                  <div className="font-extrabold text-white text-xl sm:text-2xl leading-none">
                    <CountUp value={s.value} suffix={s.suffix} />
                  </div>
                  {s.unit && <div className="text-white/80 text-xs mt-1">{s.unit}</div>}
                </motion.div>
              );
            })}
          </div>
        </Reveal>

        {/* Chart card */}
        <Reveal delay={1} className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 shadow-[0_10px_40px_rgba(18,58,114,0.06)]">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-extrabold text-navy text-lg sm:text-xl tracking-tight">
              BIST 100 ENDEKSİ — 5 YILLIK GRAFİK
            </h3>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-green-500 text-white font-bold text-sm px-3 py-1.5">
              <TrendingUp className="w-4 h-4" /> 10.245 <span>▲ %1,35</span>
            </span>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={BIST_DATA} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="bist" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E5FB4" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1E5FB4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="t"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={28}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 8px 32px rgba(18,58,114,0.12)",
                    fontFamily: "Manrope",
                  }}
                  labelStyle={{ color: "#123A72", fontWeight: 700 }}
                  formatter={(v) => [v.toLocaleString("tr-TR"), "BIST 100"]}
                />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke="#1E5FB4"
                  strokeWidth={3}
                  fill="url(#bist)"
                  dot={false}
                  activeDot={{ r: 5, fill: "#1E5FB4", stroke: "#fff", strokeWidth: 2 }}
                  isAnimationActive
                  animationDuration={1600}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
